import { TpCanonicalTypeInfo, TpDataTableColumn, VALTYPES, TpDataTable } from '../../../data/interfaces';
import { CATEGORICAL_ORDERS, sortCategories, TpCategoryInfo } from './categoricalSorters';
import { convertColumnDataToDisplayString, isSelectedItem } from './misc';

export interface TpCategoriesMapping {
  categories: TpCategoryInfo[];
  cat2Index: { [value: string]: number };
}

export const PLACEHOLDER_OTHER = '<_other_>';

/**
 * Get a list of categories for a given column in a data table, filtered by the provided filteredItemsMap.
 * @param dataTable
 * @param colInfo
 * @param filteredItemsMap
 * @returns
 */
export function getFilteredCategories(
  dataTable: TpDataTable,
  colInfo: TpDataTableColumn,
  filteredItemsMap: { [id: string]: boolean },
): TpCategoryInfo[] {
  const dataItems = dataTable.columnData[dataTable.colItemId];
  const dataCategories = dataTable.columnData[colInfo.id];

  const categories: TpCategoryInfo[] = [];
  const cat2Index: { [value: string]: number } = {};

  const multiValued = colInfo.valueType == VALTYPES.MULTICAT;
  for (let i = 0; i < dataItems.length; i++) {
    if (!filteredItemsMap[dataItems[i]]) continue; // Skip items not in the filtered set

    const catValueList = !multiValued ? [dataCategories[i]] : dataCategories[i];
    for (const catValue of catValueList) {
      if (cat2Index[catValue] === undefined) {
        categories.push({
          value: catValue,
          displayString: convertColumnDataToDisplayString(colInfo, catValue),
          count: 0,
          countSelected: 0,
        });
        cat2Index[catValue] = categories.length - 1;
      }
      const cat = categories[cat2Index[catValue]];
      cat.count++;
    }
  }

  return categories;
}

export function createCategories(
  canonicalTypeInfo: TpCanonicalTypeInfo,
  dataCategories: any[],
  colInfo: TpDataTableColumn,
  maxCatCount: number | null,
  dataItemId: any[],
  sortBy: CATEGORICAL_ORDERS = CATEGORICAL_ORDERS.Count,
): TpCategoriesMapping {
  const selectedItemsMap = canonicalTypeInfo.selectedItemsMap;

  const categories: TpCategoryInfo[] = [];
  let cat2Index: { [value: string]: number } = {};

  const multiValued = colInfo.valueType == VALTYPES.MULTICAT;
  for (let i = 0; i < dataCategories.length; i++) {
    const catValueList = !multiValued ? [dataCategories[i]] : dataCategories[i];
    for (const catValue of catValueList) {
      if (cat2Index[catValue] === undefined) {
        categories.push({
          value: catValue,
          displayString: convertColumnDataToDisplayString(colInfo, catValue),
          count: 0,
          countSelected: 0,
        });
        cat2Index[catValue] = categories.length - 1;
      }
      const cat = categories[cat2Index[catValue]];
      cat.count++;
      if (isSelectedItem(selectedItemsMap, dataItemId[i])) cat.countSelected++;
    }
  }

  sortCategories(categories, sortBy);

  // Rebuild index, because order has been changed
  cat2Index = {};
  categories.map((cat, idx) => {
    cat2Index[cat.value] = idx;
  });

  let categoriesClipped = categories;

  if (maxCatCount !== null && categories.length > maxCatCount) {
    categoriesClipped = categories.slice(0, maxCatCount);
    categoriesClipped.push({ value: PLACEHOLDER_OTHER, displayString: 'Other', count: 0, countSelected: 0 });
    cat2Index = {};
    for (let catNr = 0; catNr < maxCatCount; catNr++) cat2Index[categoriesClipped[catNr].value] = catNr;
    for (let catNr = maxCatCount; catNr < categories.length; catNr++) {
      cat2Index[categories[catNr].value] = categoriesClipped.length - 1;
      categoriesClipped[categoriesClipped.length - 1].count += categories[catNr].count;
      categoriesClipped[categoriesClipped.length - 1].countSelected += categories[catNr].countSelected;
    }
  }

  return {
    categories: categoriesClipped,
    cat2Index,
  };
}

export interface TpContingencyCell {
  count: number;
  countSelected: number;
  valX: any;
  valY: any;
  //itemIdMap: { [id: string]: boolean };
}

export interface TpContingencyTable {
  categoriesX: TpCategoriesMapping;
  categoriesY: TpCategoriesMapping;
  cells: TpContingencyCell[][];
  maxCellCount: number;
}

export function createContingencyTable(
  canonicalTypeInfo: TpCanonicalTypeInfo,
  dataCatsX: any[],
  colInfoCategoriesX: TpDataTableColumn,
  sortXBy: CATEGORICAL_ORDERS,
  maxCatCountX: number,
  dataCatsY: any[],
  colInfoCategoriesY: TpDataTableColumn,
  sortYBy: CATEGORICAL_ORDERS,
  maxCatCountY: number,
  dataItemId: any[],
): TpContingencyTable {
  const itemCount = dataCatsX.length;
  const selectedItemsMap = canonicalTypeInfo.selectedItemsMap;

  const categoriesX = createCategories(
    canonicalTypeInfo,
    dataCatsX,
    colInfoCategoriesX,
    maxCatCountX,
    dataItemId,
    sortXBy,
  );
  const categoriesY = createCategories(
    canonicalTypeInfo,
    dataCatsY,
    colInfoCategoriesY,
    maxCatCountY,
    dataItemId,
    sortYBy,
  );

  const cells: TpContingencyCell[][] = [];
  for (let idxX = 0; idxX < categoriesX.categories.length; idxX++) {
    const arr: TpContingencyCell[] = [];
    for (let idxY = 0; idxY < categoriesY.categories.length; idxY++)
      arr.push({
        count: 0,
        countSelected: 0,
        // itemIdMap: {},
        valX: categoriesX.categories[idxX].value,
        valY: categoriesY.categories[idxY].value,
      });
    cells.push(arr);
  }

  const multiValuedCatsX = colInfoCategoriesX.valueType == VALTYPES.MULTICAT;
  const multiValuedCatsY = colInfoCategoriesY.valueType == VALTYPES.MULTICAT;
  const cat2IndexX = categoriesX.cat2Index;
  const cat2IndexY = categoriesY.cat2Index;

  for (let i = 0; i < itemCount; i++) {
    const catValueXList = !multiValuedCatsX ? [dataCatsX[i]] : dataCatsX[i];
    const catValueYList = !multiValuedCatsY ? [dataCatsY[i]] : dataCatsY[i];
    for (const catValueX of catValueXList) {
      const idxX = cat2IndexX[catValueX];
      for (const catValueY of catValueYList) {
        const idxY = cat2IndexY[catValueY];
        cells[idxX][idxY].count++;
        // cells[idxX][idxY].itemIdMap[dataItemId[i]] = true;
        if (isSelectedItem(selectedItemsMap, dataItemId[i])) {
          cells[idxX][idxY].countSelected++;
        }
      }
    }
  }

  const categoriesXData = categoriesX.categories;
  const catCountX = categoriesXData.length;
  const categoriesYData = categoriesY.categories;
  const catCountY = categoriesYData.length;

  let maxCellCount = 1;
  for (let idxX = 0; idxX < catCountX; idxX++) {
    for (let idxY = 0; idxY < catCountY; idxY++) {
      if (categoriesXData[idxX].value != PLACEHOLDER_OTHER && categoriesYData[idxY].value != PLACEHOLDER_OTHER)
        maxCellCount = Math.max(maxCellCount, cells[idxX][idxY].count);
    }
  }

  const table: TpContingencyTable = {
    categoriesX,
    categoriesY,
    cells,
    maxCellCount,
  };

  return table;
}

/**
 * Generate a Blob object representing the categories data in CSV format.
 * @param categories Array of category information objects.
 * @returns A Blob object containing the CSV data.
 */
export function getCategoriesAsBlob(categories: TpCategoryInfo[]): Blob {
  const headers = ['Category', 'Count'];
  const csvData = [];
  csvData.push(headers.join(','));
  categories.forEach((category: any) => csvData.push([category.displayString, category.count]));
  const csvContent = csvData.join('\n');
  return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
}
